import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { JenisUkt, NominalUkt, Unitkerja } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import {
  // getListJalurJenis,
  getListJenisUkt,
  getListUnitkerja,
} from "@/services/getData"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import Combobox from "@/components/Combobox"

const postData = async (newData: NominalUkt) => {
  const response = await api.post("/master/nominal-ukt/store", newData)
  return response.data
}

const patchData = async (newData: NominalUkt) => {
  const response = await api.patch("/master/nominal-ukt/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_nominal_ukt: string }) => {
  const response = await api({
    url: "/master/nominal-ukt/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: NominalUkt | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<NominalUkt>({
    id_ref_nominal_ukt: selectedRow?.id_ref_nominal_ukt || "",
    id_ref_jenis_ukt: selectedRow?.id_ref_jenis_ukt || "",
    // id_ref_jalur_jenis: selectedRow?.id_ref_jalur_jenis || "",
    uk_id: selectedRow?.uk_id || "",
    kode_jenis_ukt: selectedRow?.kode_jenis_ukt || "",
    tahun_masuk:
      selectedRow?.tahun_masuk || `${new Date().getFullYear()}` || "",
    nominal_ukt: selectedRow?.nominal_ukt || "",
    angkatan: selectedRow?.angkatan || "",
  })
  const [openListProdi, setOpenListProdi] = useState(false)

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  // const { data: dataJalurJenis } = useQuery({
  //   queryKey: ["listJalurJenis"],
  //   queryFn: getListJalurJenis,
  // });

  const { data: dataJenisUkt } = useQuery({
    queryKey: ["listJenisUkt"],
    queryFn: getListJenisUkt,
  })

  const { data: dataProdi } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
    select: (data) =>
      data?.map((unitkerja: Unitkerja) => ({
        label: unitkerja.nama,
        value: `${unitkerja.uk_id}`,
        unitkerjaGroupKey: unitkerja.unitkerja_group_key,
        isActive: unitkerja.is_active,
      })),
  })

  const filteredDataProdi = dataProdi?.filter(
    (option: {
      label: string
      value: string
      unitkerjaGroupKey: string
      isActive: boolean
    }) => {
      return option?.unitkerjaGroupKey == "5" && option.isActive
    }
  )

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listNominalUkt"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data NominalUkt"
          description={`Apakah anda yakin akan menghapus data Nominal UKT "${selectedRow?.nominal_ukt}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Nominal UKT" : "Update Data Nominal UKT"}
      />
      {/* <div>
        <Label>Jalur Jenis</Label>
        <Select
          value={form.id_ref_jalur_jenis}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              id_ref_jalur_jenis: value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Jalur Jenis">
              {form.id_ref_jalur_jenis &&
                dataJalurJenis?.find(
                  (jalurJenis: JalurJenis) =>
                    jalurJenis.id_ref_jalur_jenis === form.id_ref_jalur_jenis
                )?.nama_jalur_jenis}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {dataJalurJenis?.map((jalurJenis: JalurJenis, index: number) => (
              <SelectItem
                key={index}
                value={`${jalurJenis.id_ref_jalur_jenis}`}
              >
                {jalurJenis?.nama_jalur_jenis}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div> */}

      <div>
        <Label>Prodi</Label>
        <Combobox
          open={openListProdi}
          setOpen={setOpenListProdi}
          value={form.uk_id || ""}
          setValue={(value) => {
            setForm((prev) => ({ ...prev, uk_id: value }))
          }}
          options={filteredDataProdi}
          placeholder="Pilih prodi"
        />
      </div>

      <div>
        <Label>Jenis UKT</Label>
        <Select
          value={form.id_ref_jenis_ukt}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              id_ref_jenis_ukt: value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Jalur Jenis">
              {form.id_ref_jenis_ukt &&
                dataJenisUkt?.find(
                  (jenisUkt: JenisUkt) =>
                    jenisUkt.id_ref_jenis_ukt === form.id_ref_jenis_ukt
                )?.nama_jenis_ukt}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {dataJenisUkt?.map((jenisUkt: JenisUkt, index: number) => (
              <SelectItem key={index} value={`${jenisUkt.id_ref_jenis_ukt}`}>
                {jenisUkt?.nama_jenis_ukt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Tahun Masuk</Label>
        <Input
          required
          maxLength={4}
          placeholder="Ketik tahun masuk..."
          value={form.tahun_masuk}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, tahun_masuk: event.target.value }))
          }
        />
      </div>

      <div>
        <Label>Nominal UKT</Label>
        <Input
          required
          placeholder="Ketik nominal ukt..."
          value={form.nominal_ukt}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, nominal_ukt: event.target.value }))
          }
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
